import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Demo2ZoneMap from '../Demo2ZoneMap';
import Demo2PhotoLightbox from '../Demo2PhotoLightbox';
import { Demo2ZoneRegForm } from '../panels/Demo2ZoneRegForm';
import { useDemo2Ctx } from '../Demo2Context';
import { TREES, STATUS_LABEL, STATUS_COLOR, MONITOR_LABEL, MONITOR_ICON_RI, treeIconRi, zonePhotos, type SitePhoto, type LatLng, type ZoneCategory, type MoistStatus } from '../demo2Data';

/** 관리구역 상세패널의 현장사진 슬라이드 — 슬라이드 폭을 82%로 두어 다음(뒷) 사진이 살짝 보이는 형태.
 * 사진을 클릭하면 공통 라이트박스로 크게 볼 수 있다. */
function SitePhotoSlider({ photos }: { photos: SitePhoto[] }) {
    const [idx, setIdx] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const photo = photos[idx];
    const prev = () => setIdx(i => (i - 1 + photos.length) % photos.length);
    const next = () => setIdx(i => (i + 1) % photos.length);
    return (
        <div className="site-photo-slider">
            <div className="site-photo-viewport">
                <div className="site-photo-track" style={{ transform: `translateX(calc(-${idx} * (82% + 8px)))` }}>
                    {photos.map((p, i) => (
                        <div className="site-photo-slide" key={i} onClick={() => setLightboxOpen(true)}>
                            <img src={p.src} alt={p.caption} loading="lazy" />
                        </div>
                    ))}
                </div>
                <button className="site-photo-nav prev" onClick={prev} aria-label="이전 사진"><i className="ri-arrow-left-s-line" /></button>
                <button className="site-photo-nav next" onClick={next} aria-label="다음 사진"><i className="ri-arrow-right-s-line" /></button>
                <div className="site-photo-counter">{idx + 1} / {photos.length}</div>
            </div>
            <div className="site-photo-caption">{photo.caption}</div>
            <div className="site-photo-dots">
                {photos.map((_, i) => (
                    <span key={i} className={`site-photo-dot${i === idx ? ' active' : ''}`} onClick={() => setIdx(i)} />
                ))}
            </div>
            {lightboxOpen && (
                <Demo2PhotoLightbox photos={photos} index={idx} onClose={() => setLightboxOpen(false)} onIndexChange={setIdx} />
            )}
        </div>
    );
}

const CATS: { key: ZoneCategory | 'all'; label: string; ico: string }[] = [
    { key: 'all', label: '전체 구분', ico: 'ri-apps-line' }, { key: '공원', label: '공원', ico: 'ri-plant-line' },
    { key: '가로수', label: '가로수', ico: 'ri-tree-line' }, { key: '조경수', label: '조경수', ico: 'ri-leaf-line' },
];
const STATS: { key: MoistStatus | 'all'; label: string }[] = [
    { key: 'all', label: '전체 상태' }, { key: 'good', label: '양호' },
    { key: 'caution', label: '주의' }, { key: 'warn', label: '위험' }, { key: 'danger', label: '심각' },
];

export default function Demo2Map() {
    const navigate = useNavigate();
    const location = useLocation();
    const { zones, openPanel } = useDemo2Ctx();
    const incomingZoneId = (location.state as { zoneId?: string } | null)?.zoneId;
    const [catFilter, setCatFilter] = useState<ZoneCategory | 'all'>('all');
    const [statFilter, setStatFilter] = useState<MoistStatus | 'all'>('all');
    const [selectedId, setSelectedId] = useState<string>(incomingZoneId ?? 'Z04');
    const [isDrawing, setIsDrawing] = useState(false);

    const filtered = zones.filter(z => (catFilter === 'all' || z.category === catFilter) && (statFilter === 'all' || z.status === statFilter));
    const z = zones.find(zz => zz.id === selectedId) ?? zones[0];
    const c = STATUS_COLOR[z.status];
    const circumference = 2 * Math.PI * 37;
    const offset = circumference - (z.moist / 100) * circumference;
    const treesOfZone = TREES.filter(t => t.zone === z.id);

    const handleDrawingComplete = (pts: LatLng[]) => {
        setIsDrawing(false);
        openPanel({
            title: '＋ 신규 관리구역 등록',
            body: <Demo2ZoneRegForm polygon={pts} />,
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className="list-bar">
                <div className="chips">
                    {CATS.map(o => (
                        <div key={o.key} className={`chip${catFilter === o.key ? ' active' : ''}`} onClick={() => setCatFilter(o.key)}><i className={o.ico} /> {o.label}</div>
                    ))}
                </div>
                <div style={{ width: 1, height: 22, background: 'var(--border)' }} />
                <div className="chips">
                    {STATS.map(o => (
                        <div key={o.key} className={`chip${statFilter === o.key ? ' active' : ''}`} onClick={() => setStatFilter(o.key)}>{o.label}</div>
                    ))}
                </div>
                <div style={{ flex: 1 }} />
                <span className="text-xs text-muted">{filtered.length} / {zones.length}개 구역 표시 중</span>
                {isDrawing ? (
                    <button className="btn btn-danger btn-sm" onClick={() => setIsDrawing(false)}><i className="ri-close-line" /> 그리기 취소</button>
                ) : (
                    <button className="btn btn-primary btn-sm" onClick={() => setIsDrawing(true)}><i className="ri-add-line" /> 관리구역 등록</button>
                )}
            </div>

            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div className="map-wrap">
                    <Demo2ZoneMap
                        zones={filtered}
                        selectedId={selectedId}
                        onSelectZone={setSelectedId}
                        zoom={16}
                        mapTypeToggle
                        defaultMapType="SATELLITE"
                        showWeather
                        drawingMode={isDrawing}
                        onDrawingComplete={handleDrawingComplete}
                    />
                    <div className="map-legend">
                        <div className="legend-ttl">수분 상태</div>
                        <div className="legend-row"><span className="legend-sq" style={{ background: '#1E8B53' }} />양호</div>
                        <div className="legend-row"><span className="legend-sq" style={{ background: '#2563EB' }} />주의</div>
                        <div className="legend-row"><span className="legend-sq" style={{ background: '#D97706' }} />위험</div>
                        <div className="legend-row"><span className="legend-sq" style={{ background: '#DC2626' }} />심각</div>
                    </div>
                </div>

                <div style={{ width: 390, flexShrink: 0, background: 'var(--surface)', borderLeft: '1.5px solid var(--border)', overflowY: 'auto' }}>
                    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                <div className="d2-ico d2-ico-md d2-ico-green"><i className={treeIconRi(z.treeName)} /></div>
                                <div style={{ fontSize: 15, fontWeight: 800 }}>{z.name}</div>
                            </div>
                            <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>{z.district} · {z.category}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <span className={`badge badge-${z.status}`}><span className={`badge-dot dot-${z.status}`} />{STATUS_LABEL[z.status]}</span>
                            <span className="badge badge-neutral">담당 {z.mgr}</span>
                            <span className="badge badge-neutral"><i className={MONITOR_ICON_RI[z.monitorType]} /> {MONITOR_LABEL[z.monitorType]}</span>
                        </div>
                        <div className="metric-grid">
                            <div className="metric-item"><div className="metric-lbl">면적</div><div className="metric-val" style={{ fontSize: 14 }}>{z.area.toLocaleString()}㎡</div></div>
                            <div className="metric-item"><div className="metric-lbl">총 식재수</div><div className="metric-val">{z.treeCount}주</div></div>
                        </div>
                        <div className="data-card">
                            <div className="data-card-hd"><span className="data-card-ttl">현재 날씨</span></div>
                            <div className="wx-top">
                                <div className="wx-icon">{z.weather}</div><div className="wx-temp">{z.temp}°C</div>
                                <div><div className="wx-desc">현장 관측 기준</div><div className="wx-detail">토양유형 {z.soilType} · 배수 {z.drainage}</div></div>
                            </div>
                        </div>
                        <div className="data-card data-card-compact">
                            <div className="data-card-hd"><span className="data-card-ttl">현장사진</span><span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{zonePhotos(z.id).length}장</span></div>
                            <SitePhotoSlider key={z.id} photos={zonePhotos(z.id)} />
                        </div>
                        <div className="data-card">
                            <div className="data-card-hd"><span className="data-card-ttl">수분 상태</span></div>
                            <div className="gauge-wrap" style={{ marginBottom: 12 }}>
                                <div className="gauge-ring">
                                    <svg viewBox="0 0 84 84">
                                        <circle className="g-bg" cx={42} cy={42} r={37} />
                                        <circle className="g-fill" cx={42} cy={42} r={37} stroke={c} strokeDasharray={circumference} strokeDashoffset={offset} />
                                    </svg>
                                    <div className="gauge-val"><div className="gauge-num" style={{ color: c }}>{z.moist}</div><div className="gauge-unit">%</div></div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>{STATUS_LABEL[z.status]} 상태</div>
                                    <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>권고 수분율 55~75%<br />마지막 관개 {z.lastDate}</div>
                                </div>
                            </div>
                            <div className="soil-layers">
                                {[
                                    { depth: '0-10cm', pct: Math.max(0, z.moist - 8) },
                                    { depth: '10-20cm', pct: Math.min(99, z.moist + 3) },
                                    { depth: '20-30cm', pct: Math.min(99, z.moist + 9) },
                                ].map(row => (
                                    <div key={row.depth} className="soil-row">
                                        <div className="soil-depth">{row.depth}</div>
                                        <div className="soil-bar-bg"><div className="soil-bar-fill" style={{ width: `${row.pct}%`, background: c }} /></div>
                                        <div className="soil-pct">{row.pct}%</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={`hero-box hero-box-${z.status === 'danger' ? 'danger' : z.status === 'warn' ? 'warn' : 'good'}`}>
                            <div className="hero-status" style={{ color: c }}><i className={z.status === 'danger' || z.status === 'warn' ? 'ri-alarm-warning-line' : 'ri-checkbox-circle-line'} /> {z.status === 'danger' || z.status === 'warn' ? '관개 조치 필요' : '양호 상태 유지 중'}</div>
                            <div className="hero-desc">{z.status === 'danger' ? '토양 수분율이 심각 수준입니다. 즉시 관개 작업을 지시하세요.' : z.status === 'warn' ? '수분율이 위험 구간입니다. 2~3일 이내 관개를 권장합니다.' : '현재 수분율이 적정 범위에 있습니다. 정기 점검만 유지하세요.'}</div>
                            <button className="btn btn-primary w-full" onClick={() => navigate('/demo2/web/work-management')}><i className="ri-drop-line" /> 관개 작업지시 생성</button>
                        </div>
                        <div className="data-card">
                            <div className="data-card-hd"><span className="data-card-ttl">구역 내 등록 개체목</span><span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{treesOfZone.length}주 샘플</span></div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {treesOfZone.map(t => (
                                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 9px', background: 'var(--surface2)', borderRadius: 7, cursor: 'pointer' }}
                                        onClick={() => navigate(`/demo2/web/tree-detail/${t.id}`)}>
                                        <span style={{ fontSize: 12 }}><i className={treeIconRi(t.species)} /> {t.species} <span style={{ color: 'var(--text3)' }}>{t.code}</span></span>
                                        <span style={{ fontSize: 11.5, fontWeight: 700, color: STATUS_COLOR[t.status] }}>{t.moist}%</span>
                                    </div>
                                ))}
                            </div>
                            <button className="btn btn-secondary btn-sm w-full mt-2" onClick={() => navigate('/demo2/web/tree-list')}>전체 수목 목록 보기</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
